import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { Box, Button, Heading, Flex, Text } from '@theme-ui/components';
import { Chip, Icon, NavLink } from '../atoms';
import { typography, colors } from '../../styles/theme';
import { useResponsiveValue } from '../../utils/media-query';

export const MenuItems = ({
	nodes,
	currentPath,
	pageContext,
	defaultListLength = 10,
}) => {
	const prefix = '/resources/';
	const regionPathPrefix = pageContext.regionPath
		? `/${pageContext.regionPath}`
		: '';
	const totalItems = nodes.length;
	const [visibleItems, setVisibleItems] = useState(defaultListLength);

	return (
		<>
			{nodes.slice(0, visibleItems).map(({ name, slug }, index) => {
				slug = `${regionPathPrefix}${prefix}${slug}/`;
				const isSelected = currentPath === slug;
				return (
					<Box
						key={`${name}-${index}`}
						sx={{
							...styles.menuItems,
							...styles.linkStyle,
							borderBottom:
								index === totalItems - 1
									? 'none'
									: `1px solid ${colors.neutral200}`,
							bg: isSelected
								? [
										colors.primary100,
										colors.primary100,
										colors.white,
										colors.white,
								  ]
								: colors.white,
							a: {
								color: isSelected
									? colors.primaryMain
									: [
											colors.black,
											colors.black,
											colors.primaryMain,
											colors.primaryMain,
									  ],
							},
						}}
					>
						<NavLink
							to={slug}
							customStyles={
								isSelected
									? { ...styles.selectedNavLink, ...styles.navLinkTypo }
									: styles.navLinkTypo
							}
						>
							{name}
						</NavLink>
					</Box>
				);
			})}
			{totalItems > defaultListLength && (
				<ListToggler
					defaultListLength={defaultListLength}
					setVisibleItems={setVisibleItems}
					totalItems={totalItems}
				/>
			)}
		</>
	);
};

const ListToggler = ({ defaultListLength, totalItems, setVisibleItems }) => {
	const [expanded, setExpanded] = useState(false);

	const showMore = () => {
		setVisibleItems(totalItems);
		setExpanded(true);
	};

	const showLess = () => {
		setVisibleItems(defaultListLength);
		setExpanded(false);
	};

	return (
		<Box sx={{ pt: '1.125rem' }}>
			<Button
				onClick={expanded ? showLess : showMore}
				sx={styles.toggleBtn}
			>
				<Chip
					label={expanded ? 'See Less' : 'See More'}
					type='ghost'
					customStyles={{
						width: '100%',
					}}
				/>
			</Button>
		</Box>
	);
};

export const MobileMenu = ({ currentPath, pageContext, allTopics }) => {
	const [showMenu, setShowMenu] = useState(false);

	const onClick = () => {
		setShowMenu(!showMenu);
	};

	return (
		<Box
			sx={{
				mb: '1.5rem',
				width: '100%',
				alignSelf: 'flex-start',
				position: 'relative',
			}}
		>
			<Button
				onClick={onClick}
				sx={styles.mobileMenuBtn}
			>
				<Flex sx={{ justifyContent: 'space-between' }}>
					<Text>All Topics</Text>
					<Text style={{ marginLeft: '5px' }}>
						<Icon
							name='ChevronDown'
							color={colors.primaryMain}
							size={24}
						/>
					</Text>
				</Flex>
			</Button>
			{showMenu && (
				<Box sx={styles.menuDropdown}>
					<MenuItems
						nodes={allTopics?.nodes}
						pageContext={pageContext}
						currentPath={currentPath}
						defaultListLength={allTopics?.nodes?.length}
					/>
				</Box>
			)}
		</Box>
	);
};

export const DesktopMenu = ({ currentPath, pageContext, allTopics }) => (
	<Box sx={styles.container}>
		<Heading
			as='h6'
			sx={{ ...typography.h6 }}
		>
			All Topics
		</Heading>
		<MenuItems
			nodes={allTopics.nodes}
			pageContext={pageContext}
			currentPath={currentPath}
		/>
	</Box>
);

export const Menu = ({ currentPath, pageContext }) => {
	const data = useStaticQuery(graphql`
		query menuItems {
			allTopics: allDatoCmsTopic(sort: { name: ASC }) {
				nodes {
					name
					slug
				}
			}
		}
	`);

	const { allTopics } = data;

	const menu = useResponsiveValue([
		<MobileMenu
			currentPath={currentPath}
			pageContext={pageContext}
			allTopics={allTopics}
		/>,
		<MobileMenu
			currentPath={currentPath}
			pageContext={pageContext}
			allTopics={allTopics}
		/>,
		<DesktopMenu
			currentPath={currentPath}
			pageContext={pageContext}
			allTopics={allTopics}
		/>,
		<DesktopMenu
			currentPath={currentPath}
			pageContext={pageContext}
			allTopics={allTopics}
		/>,
	]);

	return <Box mr={['1rem', '1rem', '2rem', '3.125rem']}>{menu}</Box>;
};

const styles = {
	container: {
		border: `1px solid ${colors.neutral200}`,
		borderRadius: '1.25rem',
		p: '1.375rem',
		height: 'max-content',
		width: '18rem',
	},
	menuItems: {
		py: 3,
		px: ['0.75rem', '0.75rem', 0, 0],
		cursor: 'pointer',
		bg: colors.white,
	},
	linkStyle: {
		a: {
			textDecoration: 'none',
		},
	},
	toggleBtn: {
		bg: 'transparent',
		borderRadius: '9999px',
		width: '100%',
		p: 0,
	},
	mobileMenuBtn: {
		width: '100%',
		bg: colors.white,
		color: colors.black,
		...typography.textMd500,
		borderRadius: '0.625rem',
		border: `1px solid ${colors.neutral300}`,
		py: '0.625rem',
		px: '0.875rem',
		option: {
			bg: colors.white,
			color: colors.black,
		},
	},
	menuDropdown: {
		bg: colors.white,
		border: `1px solid ${colors.neutral200}`,
		position: 'absolute',
		width: '100%',
		zIndex: 20,
		maxHeight: 500,
		overflow: 'scroll',
		top: '4rem',
	},
	selectedNavLink: {
		width: 'max-content',
		borderBottom: [
			'none',
			'none',
			`1px solid ${colors.primaryMain}`,
			`1px solid ${colors.primaryMain}`,
		],
	},
	navLinkTypo: {
		...typography.textMd500,
		fontWeight: [
			typography.textMd400.fontWeight,
			typography.textMd400.fontWeight,
			typography.textMd500.fontWeight,
			typography.textMd500.fontWeight,
		],
	},
};

Menu.propTypes = {
	currentPath: PropTypes.string,
	pageContext: PropTypes.object,
};

DesktopMenu.propTypes = {
	currentPath: PropTypes.string,
	pageContext: PropTypes.object,
	allTopics: PropTypes.object,
};

MobileMenu.propTypes = {
	currentPath: PropTypes.string,
	pageContext: PropTypes.object,
	allTopics: PropTypes.object,
};

MenuItems.propTypes = {
	nodes: PropTypes.array,
	currentPath: PropTypes.string,
	pageContext: PropTypes.object,
	defaultListLength: PropTypes.number,
};

ListToggler.propTypes = {
	defaultListLength: PropTypes.number,
	totalItems: PropTypes.number,
	setVisibleItems: PropTypes.func,
};
